import React, { useRef, useState, useEffect } from "react";
import { NewDynamicTab } from "../../../components/Page";
import { ExchangeAnalysisHPPCDefaultDates } from "../../../config/DefaultDates";
import { ExchangeMetrics } from "./PriceAnalysis/PriceAnalysis";
import BuyersAndSellers from "./Tabs/BuyersAndSellers";
import PowerExchangeAnalysis from "./Tabs/PowerExchangeAnalysis";
import Overview from "./Tabs/Overview";
import Realtime from "./Tabs/Realtime";
import TrendAnalysis from "./Tabs/TrendAnalysis";
import { Zap, BarChart3, Bot } from "lucide-react";

import DerivativeAnalysis from "./Tabs/DerivativeAnalysis";
import VidyutAi from "./Tabs/VidyutAi";

import PowerAnalytics from "../PowerAnalytics/PowerMarket/SimulationIndex";

const ModuleSwitcher = ({
  activeModule,
  onModuleChange,
}: {
  activeModule: string;
  onModuleChange: (module: string) => void;
}) => {
  const modules = [
    { key: "Power Analysis", label: "Power Analysis", icon: Zap },
    { key: "Exchange Analytics", label: "Exchange Analytics", icon: BarChart3 },
    { key: "Vidyut AI", label: "Vidyut AI", icon: Bot },
  ];

  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
    opacity: 0,
  });
  const activeIndex = modules.findIndex((m) => m.key === activeModule);

  useEffect(() => {
    if (containerRef.current && activeIndex !== -1) {
      const buttons = Array.from(
        containerRef.current.querySelectorAll("button"),
      ) as HTMLElement[];
      const activeBtn = buttons[activeIndex];
      if (activeBtn) {
        setIndicatorStyle({
          left: activeBtn.offsetLeft,
          width: activeBtn.offsetWidth,
          opacity: 1,
        });
      }
    } else {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
    }
  }, [activeIndex, activeModule]);

  return (
    <div
      ref={containerRef}
      className="relative z-0 inline-flex items-center gap-1 bg-gray-100 p-[1px] rounded-full shadow-inner"
    >
      <div
        className="absolute transition-all duration-300 ease-in-out bg-white rounded-full shadow-sm"
        style={{
          left: `${indicatorStyle.left}px`,
          width: `${indicatorStyle.width}px`,
          height: "calc(100% - 2px)",
          top: "1px",
          opacity: indicatorStyle.opacity,
          zIndex: -1,
        }}
      />
      {modules.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          onClick={() => onModuleChange(key)}
          className="tab-pill cursor-pointer hover:bg-gray-200/50"
          style={{ backgroundColor: "transparent" }}
          title={label}
        >
          <Icon
            size={22}
            strokeWidth={activeModule === key ? 2.5 : 2}
            color={activeModule === key ? "#f1935c" : "#9ca3af"}
          />
        </button>
      ))}
    </div>
  );
};

const NewExchange = () => {
  const [activeModule, setActiveModule] = useState(() => {
    const url = new URL(window.location.href);
    const mod = url.searchParams.get("module");
    const validModules = ["Power Analysis", "Exchange Analytics", "Vidyut AI"];
    return mod && validModules.includes(mod) ? mod : "Power Analysis";
  });

  const moduleConfig: Record<
    string,
    {
      header: string;
      tabs: Record<string, React.ReactNode>;
      trendTabs?: string[];
      hideDateFilterOnTabs?: string[];
    }
  > = {
    "Power Analysis": {
      header: "Power Analysis",
      tabs: {
        "Power Market": <PowerAnalytics />,
        Derivative: <DerivativeAnalysis />,
      },
      trendTabs: ["Derivative", "Power Market"],
      // hideDateFilterOnTabs: [] // Dates enabled for both
    },
    "Exchange Analytics": {
      header: "Exchange Analytics",
      tabs: {
        Collective: <Overview />,
        "Trend Analysis": <TrendAnalysis />,
        Realtime: <Realtime />,
        "Buyers & Sellers": <BuyersAndSellers />,
        "Entity Analysis": <PowerExchangeAnalysis />,
        Snapshot: <ExchangeMetrics />,
      },
      trendTabs: ["Trend Analysis"],
      hideDateFilterOnTabs: ["Snapshot"],
    },
    "Vidyut AI": {
      header: "Vidyut AI",
      tabs: { "Vidyut AI": <VidyutAi /> },
      hideDateFilterOnTabs: ["Vidyut AI"],
    },
  };

  const handleModuleChange = (module: string) => {
    setActiveModule(module);
    // Clear the 'tab' query parameter to let the new module's default tab take over
    const url = new URL(window.location.href);
    url.searchParams.set("module", module);
    url.searchParams.delete("tab");
    window.history.pushState({}, "", url);
  };

  const currentConfig = moduleConfig[activeModule];

  return (
    <>
      <NewDynamicTab
        headerHeight="h-[68px]"
        defaultDates={ExchangeAnalysisHPPCDefaultDates}
        headerPrefixComponent={
          <ModuleSwitcher
            activeModule={activeModule}
            onModuleChange={handleModuleChange}
          />
        }
        header={currentConfig.header}
        hideDateFilterOnTabs={currentConfig.hideDateFilterOnTabs}
        trendTabs={currentConfig.trendTabs}
        tabs={currentConfig.tabs}
      />

      {(activeModule === "Exchange Analytics" ||
        activeModule === "Power Analysis") && (
        <button
          onClick={() => handleModuleChange("Vidyut AI")}
          title="Vidyut AI"
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium text-white/80 hover:text-white transition-all duration-200 group"
          style={{
            background: "rgba(15, 32, 39, 0.85)",
            border: "1px solid rgba(241,147,92,0.25)",
            boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Zap size={13} className="text-[#f1935c]" fill="#f1935c" />
          <span className="tracking-wide">Vidyut AI</span>
        </button>
      )}
    </>
  );
};

export default NewExchange;
